import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux';
import * as hookActions from 'checkout/actions/hooks';
import { inLastStep } from '../../selectors/ui';
import { toDecimals } from '../../helpers/currency';
import { getOrderTotal } from '../../selectors/cart';

export class GoogleAnalytics4 extends Component {
    reportEvent = (type, args) => {
      let destination = this.props.config.measurement_id;
      if (!destination) {
          destination = this.props.config.floodlight_id;
      }
      this.props.reportEvent(destination)('event', type, {
          ...args,
          // route this event to this measurement ID - in case of other GA4 integrations on the page
          send_to: destination
      });
    }

    constructor(props) {
        super(props);

        this.state = {
          lastInsuranceEvent: null,
        }

        if (props.config?.measurement_id) {
          this.props.addReservationCompleteHook(this.onReservationComplete);
          this.props.addTicketsAddedToCartHook(this.onTicketsAdded);
          this.props.addProductsAddedToCartHook(this.onProductsAdded);
          this.props.addBundlesAddedToCartHook(this.onBundlesAdded);
          this.props.addTicketsRemovedFromCartHook(this.onTicketsRemoved);
          this.props.addProductsRemovedFromCartHook(this.onProductsRemoved);
          this.props.addBundlesRemovedFromCartHook(this.onBundlesRemoved);
          this.props.addStateTransitionHook(this.onStateTransition);
          this.props.addReservationClearHook(this.onReservationClear);
          this.props.addReservationExpiresInHook(this.onReservationExpiresIn);
          this.props.addCartTimerExtendedHook(this.onCartTimerExtended);
          this.props.addInsuranceOptionChangedHook(this.onInsuranceOptionChanged);
        }
    }

    render() {
        return null;
    }

    onReservationComplete = () => {
        const reservationSessionKey = `GoogleAnalytics4_${this.props.config.measurement_id}_onReservationComplete_${this.props.confirmationNumber}`;
        const sent = sessionStorage.getItem(reservationSessionKey);
        if (!sent) {
            // add_payment_info event
            if (Array.isArray(this.props.payments) && this.props.payments.length) {
                const payment = this.props.payments[0];
                if (payment?.payment_method) {
                    this.reportWholeReservationEvent('add_payment_info', {
                        payment_type: payment.payment_method,
                    });
                }
            }

            // purchase event
            this.reportWholeReservationEvent('purchase', {
              transaction_id: this.props.confirmationNumber,
              revenue: toDecimals({amount: this.props.orderTotal, currency: this.props.currency}),
              insurance: this.props.hasInsurance ? "On" : "Off",
            });

            sessionStorage.setItem(reservationSessionKey, '1');
        }
    }

    onTicketsAdded = tickets => {
        this.reportUpdateCartItemsEvent('add_to_cart', 'ticket', tickets);
    }

    onProductsAdded = products => {
        this.reportUpdateCartItemsEvent('add_to_cart', 'product', products);
    }

    onBundlesAdded = bundles => {
        this.reportUpdateCartItemsEvent('add_to_cart', 'bundle', bundles);
    }

    onTicketsRemoved = tickets => {
        this.reportUpdateCartItemsEvent('remove_from_cart', 'ticket', tickets);
    }

    onProductsRemoved = products => {
        this.reportUpdateCartItemsEvent('remove_from_cart', 'product', products);
    }

    onBundlesRemoved = bundles => {
        this.reportUpdateCartItemsEvent('remove_from_cart', 'bundle', bundles);
    }

    reportUpdateCartItemsEvent = (eventType, itemType, items) => {
        this.reportEvent(eventType, {
            currency: this.props.currency,
            value: this.getTotalCostOfItems(items),
            items: items.map(item => this.formatItem(item, itemType)),
            quantity: items.length
        });
    }

    reportWholeReservationEvent = (eventType, extraFields = {}) => {
        this.reportEvent(eventType, {
            currency: this.props.currency,
            value: toDecimals({ amount: this.props.orderTotal, currency: this.props.currency }),
            items: this.formatAllReservationItems(),
            ...extraFields,
        });
    }

    formatAllReservationItems = () => {
        const items = [];
        this.props.tickets.forEach(ticket => {
            items.push(this.formatItem(ticket, 'ticket'));
        });
        this.props.productSales.forEach(product => {
            items.push(this.formatItem(product, 'product'));
        });
        this.props.bundleSales.forEach(bundle => {
            items.push(this.formatItem(bundle, 'bundle'));
        });
        return items;
    }

    onStateTransition = page => {
        if (page === 'cart') {
            this.reportWholeReservationEvent('view_cart');
        }

        // checkout step seen action
        if (typeof page === 'string' && page.substring(0, 9) === 'checkout-' && this.props.inLastStep) {
            this.reportWholeReservationEvent('begin_checkout');
        }
    }

    onReservationExpiresIn = (expiresIn) => {
        if (expiresIn === 60) {
            this.reportEvent('expiresIn', { expiresIn });
        }
    }

    onReservationClear = (_, reason) => {
      if (reason === 'expired') {
        this.reportEvent('cartExpired');
      }

      if (reason === 'clearCart') {
        this.reportEvent('clearCart');
      }
    }

    onCartTimerExtended = () => {
      this.reportWholeReservationEvent('cartTimerExtended');
    }

    onInsuranceOptionChanged = ({ event }) => {
      const eventName = event === 'accepted' ? 'insuranceAccepted' : 'insuranceDeclined';

      if (this.state.lastInsuranceEvent === event) {
        return;
      }

      if (this.state.lastInsuranceEvent) {
        this.reportEvent('insuranceChanged', {
          from: this.state.lastInsuranceEvent,
          to: event
        })
      } else {
        this.reportEvent(eventName);
      }

      this.setState({
        lastInsuranceEvent: event
      });
    }

    formatItem = (item, type) => {

        const itemIdFieldMap = {
            ticket: 'price_level_id',
            product: 'product_id',
            bundle: 'bundle_id',
        };

        const itemNameFieldMap = {
            ticket: 'price_level_name',
            product: 'product_name',
            bundle: 'name',
        };

        return {
            item_id: item?.[itemIdFieldMap[type]] + '',
            item_name: item?.[itemNameFieldMap[type]] + '',
            item_category: type,
            price: toDecimals({ amount: +item?.cost?.amount + +item?.buyer_fee?.amount, currency: this.props.currency }),
            quantity: 1,
        };
    }

    getTotalCostOfItems = items => {
        let cost = 0;
        if (Array.isArray(items)) {
            items.forEach(item => {
                cost += +(item?.cost?.amount ?? 0);
                cost += +(item?.buyer_fee?.amount ?? 0);
            });
        }
        return toDecimals({ amount: cost, currency: this.props.currency });
    }
}

const mapStateToProps = state => {
    return {
        payments: state.payment.payments,
        confirmationNumber: state.reservation.confirmation_number,
        inLastStep: inLastStep(state),
        currency: state.reservation.currency || 'USD',
        orderTotal: getOrderTotal(state)?.amount ?? 0,
        tickets: state.tickets.tickets,
        productSales: state.productSales.products,
        bundleSales: state.bundleSales.bundles,
        hasInsurance: !!state.reservation.insuranceProvider,
    }
}

const mapDispatchToProps = dispatch => {
    const actions = {};
    Object.keys(hookActions).forEach(hookActionName => {
        actions[hookActionName] = hookActions[hookActionName];
    });
    return bindActionCreators(actions, dispatch);
}

GoogleAnalytics4.propTypes = {
    config: PropTypes.object.isRequired,
    addReservationCompleteHook: PropTypes.func,
    addTicketsAddedToCartHook: PropTypes.func,
    addProductsAddedToCartHook: PropTypes.func,
    addBundlesAddedToCartHook: PropTypes.func,
    addTicketsRemovedFromCartHook: PropTypes.func,
    addProductsRemovedFromCartHook: PropTypes.func,
    addBundlesRemovedFromCartHook: PropTypes.func,
    addStateTransitionHook: PropTypes.func,
    addReservationClearHook: PropTypes.func,
    addReservationExpiresInHook: PropTypes.func,
    addCartTimerExtendedHook: PropTypes.func,
    currency: PropTypes.string,
    orderTotal: PropTypes.number,
    hasInsurance: PropTypes.bool,
}

export default connect(mapStateToProps, mapDispatchToProps)(GoogleAnalytics4)
